// src/components/employee/EmployeePayroll.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    DollarSign, Download, Calendar, TrendingUp,
    FileText, CreditCard, PieChart, Info, X, ChevronDown, Save, Menu, Upload, Filter, Check,
    Eye, RefreshCw, AlertCircle
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFlexiRequests } from '../../contexts/FlexiRequestContext';
import { useEmployees } from '../../contexts/EmployeeContext';
import { usePayroll } from '../../contexts/PayrollContext';
import ReadOnlyCompensationView from '../common/ReadOnlyCompensationView';
import EmployeeTax from './EmployeeTax';
import payrollService from '../../services/payroll.service';

const EmployeePayroll = () => {
    const { theme } = useTheme();
    const { employees } = useEmployees();
    const { getEmployeePayroll } = usePayroll();
    const {
        addFlexiRequest,
        deleteFlexiRequest,
        getEmployeeRequests,
        currentUser
    } = useFlexiRequests();
    const [activeTab, setActiveTab] = useState('PAY PACKAGE');
    const tabLocation = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(tabLocation.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
    }, [tabLocation.search]);
    const [selectedYear, setSelectedYear] = useState('2023-24');
    const [availableYears] = useState(['2023-24', '2022-23', '2021-22', '2020-21']);
    const [loading, setLoading] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState('INR');
    const [showCTCProration, setShowCTCProration] = useState(false);
    const [selectedProrationRow, setSelectedProrationRow] = useState(null);
    const [selectedProrationYear, setSelectedProrationYear] = useState('2023');
    const [flexiCurrency, setFlexiCurrency] = useState('INR');
    const [showFlexiRequestModal, setShowFlexiRequestModal] = useState(false);
    const flexiRequests = getEmployeeRequests();
    const [newFlexiRequest, setNewFlexiRequest] = useState({
        title: '',
        amount: '',
        currency: 'INR',
        supportingDocument: null
    });
    const [editingRequest, setEditingRequest] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [statusFilter, setStatusFilter] = useState(['Open']);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);

    // Payslips state
    const [payslips, setPayslips] = useState([]);
    const [payslipsLoading, setPayslipsLoading] = useState(false);
    const [payslipsError, setPayslipsError] = useState(null);
    const [selectedPayslipYear, setSelectedPayslipYear] = useState(new Date().getFullYear());
    const [downloadingPayslip, setDownloadingPayslip] = useState(null);

    // Employee employment info - John Doe's information
    const employeeInfo = {
        joiningDate: currentUser.joiningDate || '15 Mar 2022',
        currentStatus: 'Active',
        totalDaysWorked: 580, // Since March 2022
        name: currentUser.name,
        id: currentUser.id,
        department: currentUser.department,
        designation: currentUser.designation,
        email: currentUser.email
    };

    // Currency options with conversion rates (simplified example)
    const currencies = {
        'INR': { symbol: '₹', rate: 1 },
        'USD': { symbol: '$', rate: 0.012 },
        'GBP': { symbol: '£', rate: 0.0095 },
        'CAD': { symbol: 'C$', rate: 0.016 },
        'AUD': { symbol: 'A$', rate: 0.018 },
        'MXN': { symbol: 'MX$', rate: 0.21 },
        'JPY': { symbol: '¥', rate: 1.8 },
        'EUR': { symbol: '€', rate: 0.011 }
    };

    // Employee data from FlexiRequestContext (which now uses EmployeeContext)
    const employee = currentUser;

    // Tab sections
    const tabSections = [
        'PAY PACKAGE'
        // , 'CTC PRORATION', 'FLEXI COMPONENTS', 'IT DECLARATION',
        // 'TAX SHEET'
        , 'PAY SLIPS'
        , 'TAX (TDS)'
        // , 'OFF CYCLE PAY SLIPS', 'EXTRA PAYMENTS',
        // 'LOANS', 'ADVANCES / EXTRA DEDUCTIONS', 'PERQUISITES'
    ];

    // Get actual payroll data from PayrollContext
    const employeePayrollInfo = getEmployeePayroll(currentUser.id);
    const employeeCTCBreakdown = employeePayrollInfo.ctcBreakdown;

    // Generate pay package data from CTC breakdown or use defaults
    const payPackageData = employeeCTCBreakdown ? [
        {
            date: new Date().toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            }),
            currency: 'INR(₹)',
            monthlyFixed: (employeeCTCBreakdown.monthly.basic + employeeCTCBreakdown.monthly.hra + employeeCTCBreakdown.monthly.specialAllowance).toLocaleString('en-IN'),
            totalCTC: (employeeCTCBreakdown.annual.basic + employeeCTCBreakdown.annual.hra + employeeCTCBreakdown.annual.lta + employeeCTCBreakdown.annual.mobileInternet + employeeCTCBreakdown.annual.fuelAllowance + employeeCTCBreakdown.annual.specialAllowance + employeeCTCBreakdown.annual.performanceBonus).toLocaleString('en-IN'),
            baseCurrencyCTC: (employeeCTCBreakdown.monthly.basic * 12).toLocaleString('en-IN'),
            variablePay: employeeCTCBreakdown.annual.performanceBonus.toLocaleString('en-IN'),
            salaryStructure: 'Standard Salary Structure',
            annualMonthly: 'Annual'
        }
    ] : [
        // Default data if no payroll data exists
        {
            date: new Date().toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            }),
            currency: 'INR(₹)',
            monthlyFixed: '0',
            totalCTC: '0',
            baseCurrencyCTC: '0',
            variablePay: '0',
            salaryStructure: 'No Salary Structure Defined',
            annualMonthly: 'Annual'
        }
    ];

    // CTC breakdown data from actual payroll information
    const ctcBreakdown = employeeCTCBreakdown ? {
        earnings: [
            { component: 'Basic', annual: employeeCTCBreakdown.annual.basic.toLocaleString(), monthly: employeeCTCBreakdown.monthly.basic.toLocaleString(), currency: 'INR' },
            { component: 'HRA', annual: employeeCTCBreakdown.annual.hra.toLocaleString(), monthly: employeeCTCBreakdown.monthly.hra.toLocaleString(), currency: 'INR' },
            { component: 'LTA', annual: employeeCTCBreakdown.annual.lta.toLocaleString(), monthly: employeeCTCBreakdown.monthly.lta.toLocaleString(), currency: 'INR' },
            { component: 'Mobile and Internet Allowance', annual: employeeCTCBreakdown.annual.mobileInternet.toLocaleString(), monthly: employeeCTCBreakdown.monthly.mobileInternet.toLocaleString(), currency: 'INR' },
            { component: 'Fuel Allowance', annual: employeeCTCBreakdown.annual.fuelAllowance.toLocaleString(), monthly: employeeCTCBreakdown.monthly.fuelAllowance.toLocaleString(), currency: 'INR' },
            { component: 'Special Allowance', annual: employeeCTCBreakdown.annual.specialAllowance.toLocaleString(), monthly: employeeCTCBreakdown.monthly.specialAllowance.toLocaleString(), currency: 'INR' },
            { component: 'Performance Bonus', annual: employeeCTCBreakdown.annual.performanceBonus.toLocaleString(), monthly: employeeCTCBreakdown.monthly.performanceBonus.toLocaleString(), currency: 'INR' },
        ],
        employeeGrossPay: [
            { component: 'Basic', annual: employeeCTCBreakdown.annual.basic.toLocaleString(), monthly: employeeCTCBreakdown.monthly.basic.toLocaleString() },
            { component: 'HRA', annual: employeeCTCBreakdown.annual.hra.toLocaleString(), monthly: employeeCTCBreakdown.monthly.hra.toLocaleString() },
            { component: 'LTA', annual: employeeCTCBreakdown.annual.lta.toLocaleString(), monthly: employeeCTCBreakdown.monthly.lta.toLocaleString() },
            { component: 'Mobile and Internet Allowance', annual: employeeCTCBreakdown.annual.mobileInternet.toLocaleString(), monthly: employeeCTCBreakdown.monthly.mobileInternet.toLocaleString() },
            { component: 'Fuel Allowance', annual: employeeCTCBreakdown.annual.fuelAllowance.toLocaleString(), monthly: employeeCTCBreakdown.monthly.fuelAllowance.toLocaleString() },
            { component: 'Special Allowance', annual: employeeCTCBreakdown.annual.specialAllowance.toLocaleString(), monthly: employeeCTCBreakdown.monthly.specialAllowance.toLocaleString() },
        ],
        variablePay: [
            { component: 'Annual Bonus', annual: employeeCTCBreakdown.annual.performanceBonus.toLocaleString(), monthly: employeeCTCBreakdown.monthly.performanceBonus.toLocaleString() },
        ]
    } : {
        earnings: [
            { component: 'Basic', annual: '0', monthly: '0', currency: 'INR' },
            { component: 'HRA', annual: '0', monthly: '0', currency: 'INR' },
            { component: 'LTA', annual: '0', monthly: '0', currency: 'INR' },
            { component: 'Mobile and Internet Allowance', annual: '0', monthly: '0', currency: 'INR' },
            { component: 'Fuel Allowance', annual: '0', monthly: '0', currency: 'INR' },
            { component: 'Special Allowance', annual: '0', monthly: '0', currency: 'INR' },
            { component: 'Performance Bonus', annual: '0', monthly: '0', currency: 'INR' },
        ],
        employeeGrossPay: [
            { component: 'Basic', annual: '0', monthly: '0' },
            { component: 'HRA', annual: '0', monthly: '0' },
            { component: 'LTA', annual: '0', monthly: '0' },
            { component: 'Mobile and Internet Allowance', annual: '0', monthly: '0' },
            { component: 'Fuel Allowance', annual: '0', monthly: '0' },
            { component: 'Special Allowance', annual: '0', monthly: '0' },
        ],
        variablePay: [
            { component: 'Annual Bonus', annual: '0', monthly: '0' },
        ]
    };

    // Flexi components data
    const [flexiData] = useState({
        mobileAllowance: {
            value: 25000,
            approvedAmount: 22000,
            range: '5000-25000',
            status: 'Open',
            fromDate: '01 Apr 2023',
            toDate: '31 Mar 2024'
        },
        fuelAllowance: {
            value: 30000,
            approvedAmount: 28000,
            range: '0-30000',
            status: 'Close',
            fromDate: '15 May 2023',
            toDate: '14 May 2024'
        },
        medicalAllowance: {
            value: 15000,
            approvedAmount: 15000,
            range: '0-20000',
            status: 'Under Review',
            fromDate: '01 Jun 2023',
            toDate: '31 May 2024'
        }
    });

    const formatNumber = (num) => {
        return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '0';
    };

    // Currency conversion function
    const convertCurrency = (value, fromCurrency = 'INR', toCurrency = selectedCurrency) => {
        if (!value || value === 'N.A.' || value === '') return value;

        // Remove commas and convert to number
        const numValue = parseFloat(value.replace(/,/g, ''));
        if (isNaN(numValue)) return value;

        // Convert from INR to target currency
        const convertedValue = numValue * currencies[toCurrency].rate;
        return formatNumber(Math.round(convertedValue));
    };

    // Handle CTC Proration click
    const handleCTCProrationClick = (rowIndex) => {
        setSelectedProrationRow(rowIndex);
        setShowCTCProration(true);
    };

    // Generate monthwise proration data
    const generateProrationData = (rowIndex) => {
        if (rowIndex === null || !payPackageData[rowIndex]) return [];

        const currentRow = payPackageData[rowIndex];
        const grossCTC = parseFloat(currentRow.totalCTC.replace(/,/g, ''));

        // Determine date range based on row position
        let startDate, endDate;
        if (rowIndex === 0) {
            // First row: from current row date to current month
            startDate = new Date(currentRow.date);
            endDate = new Date(); // Current date
        } else {
            // Subsequent rows: from current row date to previous row date
            startDate = new Date(currentRow.date);
            endDate = new Date(payPackageData[rowIndex - 1].date);
        }

        // Generate months between start and end dates
        const months = [];
        const currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            const monthNames = [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ];

            const workDays = 22 + Math.floor(Math.random() * 6); // Random work days 22-27
            const attendedDays = workDays - Math.floor(Math.random() * 3); // Attended days
            const dailyGross = grossCTC / 12 / workDays;
            const monthlyGross = dailyGross * workDays;
            const actualGross = dailyGross * attendedDays;
            const deductions = actualGross * 0.12; // 12% deductions
            const tds = actualGross * 0.1; // 10% TDS
            const netCTC = actualGross - deductions - tds;

            months.push({
                month: `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`,
                workDays: workDays,
                attendedDays: attendedDays,
                grossCTC: Math.round(monthlyGross),
                actualGross: Math.round(actualGross),
                deductions: Math.round(deductions),
                tds: Math.round(tds),
                netCTC: Math.round(netCTC)
            });

            currentDate.setMonth(currentDate.getMonth() + 1);
        }

        return months;
    };

    // Handle flexi component request submission
    const handleFlexiRequestSubmit = () => {
        if (!newFlexiRequest.title || !newFlexiRequest.amount) {
            alert('Please fill in all required fields');
            return;
        }

        if (editingRequest) {
            // Note: For editing, we would need an update function in the context
            // For now, just close the modal as editing existing requests might be restricted
            alert('Editing requests is currently not allowed once submitted');
            setEditingRequest(null);
        } else {
            // Create new request using shared context
            const requestData = {
                title: newFlexiRequest.title,
                amount: parseFloat(newFlexiRequest.amount),
                currency: newFlexiRequest.currency,
                supportingDocument: newFlexiRequest.supportingDocument
            };
            addFlexiRequest(requestData);
        }

        setNewFlexiRequest({ title: '', amount: '', currency: 'INR', supportingDocument: null });
        setShowFlexiRequestModal(false);
    };

    // Handle edit request
    const handleEditRequest = (request) => {
        setEditingRequest(request);
        setNewFlexiRequest({
            title: request.title,
            amount: request.amount.toString(),
            currency: request.currency,
            supportingDocument: request.supportingDocument || null
        });
        setShowFlexiRequestModal(true);
    };

    // Handle delete request
    const handleDeleteRequest = (requestId) => {
        deleteFlexiRequest(requestId);
        setShowDeleteConfirm(null);
    };

    // Convert flexi component currency
    const convertFlexiCurrency = (value, fromCurrency = 'INR', toCurrency = flexiCurrency) => {
        if (!value || value === 'N.A.' || value === '') return value;

        const numValue = parseFloat(value.toString().replace(/,/g, ''));
        if (isNaN(numValue)) return value;

        const convertedValue = numValue * currencies[toCurrency].rate;
        return formatNumber(Math.round(convertedValue));
    };

    // Handle status filter checkbox changes
    const handleStatusFilterChange = (status) => {
        setStatusFilter(prev => {
            if (prev.includes(status)) {
                // Remove if already selected
                const newFilter = prev.filter(s => s !== status);
                return newFilter.length === 0 ? ['Open'] : newFilter; // Default to 'Open' if none selected
            } else {
                // Add if not selected
                return [...prev, status];
            }
        });
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showStatusDropdown && !event.target.closest('.status-filter-container')) {
                setShowStatusDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showStatusDropdown]);

    // Fetch payslips when the PAY SLIPS tab is active or year changes
    useEffect(() => {
        if (activeTab === 'PAY SLIPS') {
            fetchPayslips();
        }
    }, [activeTab, selectedPayslipYear]);

    // Fetch payslips from backend
    const fetchPayslips = async () => {
        setPayslipsLoading(true);
        setPayslipsError(null);
        try {
            const response = await payrollService.getMyPayslips({ year: selectedPayslipYear });
            if (response.success) {
                setPayslips(response.payslips || []);
            } else {
                setPayslipsError('Failed to load payslips');
            }
        } catch (error) {
            console.error('Error fetching payslips:', error);
            setPayslipsError(error.message || 'Failed to load payslips');
        } finally {
            setPayslipsLoading(false);
        }
    };

    // Handle payslip download
    const handleDownloadPayslip = async (payslipId, fileName) => {
        setDownloadingPayslip(payslipId);
        try {
            await payrollService.downloadStoredPayslip(payslipId, fileName);
        } catch (error) {
            console.error('Error downloading payslip:', error);
            alert('Failed to download payslip: ' + error.message);
        } finally {
            setDownloadingPayslip(null);
        }
    };

    // Format currency for display
    const formatCurrencyAmount = (amount) => {
        if (amount === null || amount === undefined) return '₹0';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Get month name from month number
    const getMonthName = (month) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return months[(month - 1)] || '';
    };

    // Generate yearwise CTC proration data for dedicated CTC Proration section
    const generateYearwiseProrationData = (year) => {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];

        // Find relevant CTC data for the year
        const relevantCTCData = payPackageData.find(row =>
            new Date(row.date).getFullYear().toString() === year
        ) || payPackageData[0];

        const grossCTC = parseFloat(relevantCTCData.totalCTC.replace(/,/g, ''));

        return months.map((month, index) => {
            const workDays = 22 + Math.floor(Math.random() * 6);
            const attendedDays = workDays - Math.floor(Math.random() * 3);
            const dailyGross = grossCTC / 12 / workDays;
            const monthlyGross = dailyGross * workDays;
            const actualGross = dailyGross * attendedDays;
            const deductions = actualGross * 0.12;
            const tds = actualGross * 0.1;
            const netCTC = actualGross - deductions - tds;

            return {
                month: `${month} ${year}`,
                workDays: workDays,
                attendedDays: attendedDays,
                grossCTC: Math.round(monthlyGross),
                actualGross: Math.round(actualGross),
                deductions: Math.round(deductions),
                tds: Math.round(tds),
                netCTC: Math.round(netCTC)
            };
        });
    };

    const renderTabContent = () => {
        switch(activeTab) {
            case 'PAY PACKAGE':
                return renderPayPackage();
            case 'CTC PRORATION':
                return renderCTCProration();
            case 'FLEXI COMPONENTS':
                return renderFlexiComponents();
            case 'IT DECLARATION':
                return renderITDeclaration();
            case 'TAX SHEET':
                return renderTaxSheet();
            case 'PAY SLIPS':
                return renderPaySlips();
            case 'TAX (TDS)':
                return <EmployeeTax />;
            default:
                return <div className="px-2 py-4">Coming Soon...</div>;
        }
    };

    const renderPayPackage = () => {
        return <ReadOnlyCompensationView employeeId={currentUser.id} />;
    };

    const renderCTCProration = () => {
        const yearwiseData = generateYearwiseProrationData(selectedProrationYear);

        return (
            <div className="px-2 py-4">
                {/* Employee Information Section */}
                <div className={`border rounded-lg p-6 mb-6 ${theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
                    <h4 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Employee Employment Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                Joining Date:
                            </span>
                            <p className={`text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {employeeInfo.joiningDate}
                            </p>
                        </div>
                        <div>
                            <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                Current Employment Status:
                            </span>
                            <p className={`text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                                    {employeeInfo.currentStatus}
                                </span>
                            </p>
                        </div>
                        <div>
                            <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                Total Days Worked:
                            </span>
                            <p className={`text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {employeeInfo.totalDaysWorked} days
                            </p>
                        </div>
                    </div>
                </div>

                {/* Year Selection and CTC Proration Table */}
                <div className={`border rounded-lg p-6 ${theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
                    <div className="flex justify-between items-center mb-6">
                        <h4 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Complete Employment CTC Proration
                        </h4>
                        <div className="flex items-center gap-2">
                            <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                Select Year:
                            </label>
                            <select
                                value={selectedProrationYear}
                                onChange={(e) => setSelectedProrationYear(e.target.value)}
                                className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                                    theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                                }`}
                            >
                                <option value="2023">2023</option>
                                <option value="2022">2022</option>
                                <option value="2021">2021</option>
                            </select>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className={`min-w-full border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>
                            <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                                <tr>
                                    <th className={`px-3 py-2 text-left text-xs font-medium uppercase border-r ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Month</th>
                                    <th className={`px-3 py-2 text-left text-xs font-medium uppercase border-r ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Attended/Total Days</th>
                                    <th className={`px-3 py-2 text-left text-xs font-medium uppercase border-r ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Gross CTC</th>
                                    <th className={`px-3 py-2 text-left text-xs font-medium uppercase border-r ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Actual Gross</th>
                                    <th className={`px-3 py-2 text-left text-xs font-medium uppercase border-r ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Deductions</th>
                                    <th className={`px-3 py-2 text-left text-xs font-medium uppercase border-r ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>TDS</th>
                                    <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Net CTC</th>
                                </tr>
                            </thead>
                            <tbody className={theme === 'dark' ? 'bg-gray-800' : 'bg-white'}>
                                {yearwiseData.map((month, index) => (
                                    <tr key={index} className={`border-t ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>
                                        <td className={`px-3 py-2 border-r text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            {month.month}
                                        </td>
                                        <td className={`px-3 py-2 border-r text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            {month.attendedDays}/{month.workDays}
                                        </td>
                                        <td className={`px-3 py-2 border-r text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            {currencies[selectedCurrency].symbol}{convertCurrency(month.grossCTC.toString())}
                                        </td>
                                        <td className={`px-3 py-2 border-r text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            {currencies[selectedCurrency].symbol}{convertCurrency(month.actualGross.toString())}
                                        </td>
                                        <td className={`px-3 py-2 border-r text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            {currencies[selectedCurrency].symbol}{convertCurrency(month.deductions.toString())}
                                        </td>
                                        <td className={`px-3 py-2 border-r text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            {currencies[selectedCurrency].symbol}{convertCurrency(month.tds.toString())}
                                        </td>
                                        <td className={`px-3 py-2 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            {currencies[selectedCurrency].symbol}{convertCurrency(month.netCTC.toString())}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'} border-t-2`}>
                                <tr>
                                    <td className={`px-3 py-3 border-r text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        YEAR TOTALS
                                    </td>
                                    <td className={`px-3 py-3 border-r text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        {yearwiseData.reduce((sum, month) => sum + month.attendedDays, 0)}/
                                        {yearwiseData.reduce((sum, month) => sum + month.workDays, 0)}
                                    </td>
                                    <td className={`px-3 py-3 border-r text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        {currencies[selectedCurrency].symbol}{convertCurrency(
                                            yearwiseData.reduce((sum, month) => sum + month.grossCTC, 0).toString()
                                        )}
                                    </td>
                                    <td className={`px-3 py-3 border-r text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        {currencies[selectedCurrency].symbol}{convertCurrency(
                                            yearwiseData.reduce((sum, month) => sum + month.actualGross, 0).toString()
                                        )}
                                    </td>
                                    <td className={`px-3 py-3 border-r text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        {currencies[selectedCurrency].symbol}{convertCurrency(
                                            yearwiseData.reduce((sum, month) => sum + month.deductions, 0).toString()
                                        )}
                                    </td>
                                    <td className={`px-3 py-3 border-r text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        {currencies[selectedCurrency].symbol}{convertCurrency(
                                            yearwiseData.reduce((sum, month) => sum + month.tds, 0).toString()
                                        )}
                                    </td>
                                    <td className={`px-3 py-3 text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        {currencies[selectedCurrency].symbol}{convertCurrency(
                                            yearwiseData.reduce((sum, month) => sum + month.netCTC, 0).toString()
                                        )}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    const renderFlexiComponents = () => {
        return (
            <div className="px-2 py-4">
                {/* Currency Selection Header */}
                <div className="flex justify-between items-center mb-6">
                    <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Flexi Components
                    </h3>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                Currency:
                            </label>
                            <select
                                value={flexiCurrency}
                                onChange={(e) => setFlexiCurrency(e.target.value)}
                                className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                                    theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                                }`}
                            >
                                {Object.keys(currencies).map(currency => (
                                    <option key={currency} value={currency}>
                                        {currency} ({currencies[currency].symbol})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button
                            onClick={() => setShowFlexiRequestModal(true)}
                            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Raise Flexi Component Request
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Flexi Declaration Form */}
                    <div className={`border rounded-lg p-6 ${theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
                        <div className="mb-6">
                            <h4 className={`font-medium text-blue-600 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                                {currentUser.name}'s
                            </h4>
                            <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Flexi Payroll Declarations
                            </h4>
                        </div>

                        {/* Status Filter */}
                        <div className="mb-4 status-filter-container">
                            <div className="flex items-center gap-2 mb-2">
                                <Filter size={16} className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} />
                                <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Filter by Status:
                                </label>
                                <button
                                    onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                                    className={`px-3 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 flex items-center gap-2 ${
                                        theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' : 'bg-white border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    <span>{statusFilter.length === 1 ? statusFilter[0] : `${statusFilter.length} selected`}</span>
                                    <ChevronDown size={14} className={`transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
                                </button>
                            </div>

                            {showStatusDropdown && (
                                <div className={`border rounded-lg p-2 space-y-2 ${
                                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                                }`}>
                                    {['Open', 'Close', 'Under Review'].map(status => (
                                        <label key={status} className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={statusFilter.includes(status)}
                                                onChange={() => handleStatusFilterChange(status)}
                                                className="sr-only"
                                            />
                                            <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                                                statusFilter.includes(status)
                                                    ? 'bg-blue-600 border-blue-600'
                                                    : theme === 'dark' ? 'border-gray-500' : 'border-gray-300'
                                            }`}>
                                                {statusFilter.includes(status) && <Check size={12} className="text-white" />}
                                            </div>
                                            <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {status}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="space-y-4">
                            {Object.entries(flexiData)
                                .filter(([key, data]) => statusFilter.includes(data.status))
                                .map(([key, data]) => (
                                <div key={key} className={`border rounded-lg p-4 ${
                                    theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                                }`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <h5 className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            {key === 'mobileAllowance' ? 'Mobile And Internet Allowance' :
                                             key === 'fuelAllowance' ? 'Fuel Allowance' :
                                             key === 'medicalAllowance' ? 'Medical Allowance' : key}
                                        </h5>
                                        <span className={`inline-block px-2 py-1 text-xs rounded ${
                                            data.status === 'Open'
                                                ? 'bg-green-100 text-green-800'
                                                : data.status === 'Close'
                                                ? 'bg-red-100 text-red-800'
                                                : data.status === 'Under Review'
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {data.status}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className={`block text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                Range: {data.range}
                                            </span>
                                            <span className={`block font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                Value: {currencies[flexiCurrency].symbol}{convertFlexiCurrency(data.value)}
                                            </span>
                                        </div>
                                        <div>
                                            <span className={`block text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                Period: {data.fromDate} to {data.toDate}
                                            </span>
                                            {(data.status === 'Open' || data.status === 'Close') && (
                                                <span className={`block font-medium ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                                                    Approved: {currencies[flexiCurrency].symbol}{convertFlexiCurrency(data.approvedAmount)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {Object.entries(flexiData).filter(([key, data]) => statusFilter.includes(data.status)).length === 0 && (
                                <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                    <FileText size={48} className="mx-auto mb-4 opacity-50" />
                                    <p>No flexi components with selected status filters.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Requests Sub-section */}
                    <div className={`border rounded-lg p-6 ${theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
                        <h4 className={`font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Flexi Component Requests
                        </h4>

                        {flexiRequests.length === 0 ? (
                            <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                <FileText size={48} className="mx-auto mb-4 opacity-50" />
                                <p>No flexi component requests submitted yet.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {flexiRequests.map((request) => (
                                    <div key={request.id} className={`border rounded-lg p-4 ${
                                        theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                                    }`}>
                                        <div className="flex justify-between items-start mb-2">
                                            <h5 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {request.title}
                                            </h5>
                                            <div className="flex items-center gap-2">
                                                <span className={`px-2 py-1 text-xs rounded ${
                                                    request.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                                    request.status === 'Under-review' ? 'bg-blue-100 text-blue-800' :
                                                    request.status === 'Dismiss' ? 'bg-red-100 text-red-800' :
                                                    request.status === 'Open' ? 'bg-green-100 text-green-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {request.status}
                                                </span>
                                                {request.status === 'Pending' && (
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => handleEditRequest(request)}
                                                            className={`p-1 rounded hover:bg-opacity-20 ${
                                                                theme === 'dark' ? 'text-blue-400 hover:bg-blue-400' : 'text-blue-600 hover:bg-blue-600'
                                                            }`}
                                                            title="Edit Request"
                                                        >
                                                            <FileText size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => setShowDeleteConfirm(request.id)}
                                                            className={`p-1 rounded hover:bg-opacity-20 ${
                                                                theme === 'dark' ? 'text-red-400 hover:bg-red-400' : 'text-red-600 hover:bg-red-600'
                                                            }`}
                                                            title="Delete Request"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                            Amount: {currencies[request.currency].symbol}{formatNumber(request.amount)}
                                        </p>
                                        <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Requested on: {request.requestDate}
                                        </p>
                                        {request.status === 'Pending' && (
                                            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-orange-400' : 'text-orange-600'}`}>
                                                Pending status with [{request.reportingManager}]
                                            </p>
                                        )}
                                        {request.status === 'Under Review' && (
                                            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                                                Under review by [{request.reportingManager}]
                                            </p>
                                        )}
                                        {request.status === 'Dismiss' && request.dismissReason && (
                                            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
                                                Dismissed: {request.dismissReason}
                                            </p>
                                        )}
                                        {request.supportingDocument && (
                                            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                                                Supporting document: {request.supportingDocument.name}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderITDeclaration = () => {
        return (
            <div className="px-2 py-4">
                <div className="text-center text-gray-500">
                    <FileText size={48} className="mx-auto mb-4 opacity-50" />
                    <p>IT Declaration section coming soon...</p>
                </div>
            </div>
        );
    };

    const renderTaxSheet = () => {
        return (
            <div className="px-2 py-4">
                <div className="text-center text-gray-500">
                    <FileText size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Tax Sheet section coming soon...</p>
                </div>
            </div>
        );
    };

    const renderPaySlips = () => {
        // Generate available years (current year and 4 years back)
        const currentYear = new Date().getFullYear();
        const availablePayslipYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

        return (
            <div className="px-2 py-4">
                {/* Header with Year Filter */}
                <div className="flex justify-between items-center mb-6">
                    <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        My Pay Slips
                    </h3>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                Year:
                            </label>
                            <select
                                value={selectedPayslipYear}
                                onChange={(e) => setSelectedPayslipYear(parseInt(e.target.value))}
                                className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                                    theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                                }`}
                            >
                                {availablePayslipYears.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            onClick={fetchPayslips}
                            disabled={payslipsLoading}
                            className={`p-2 rounded-lg transition-colors ${
                                theme === 'dark'
                                    ? 'text-gray-300 hover:bg-gray-700'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                            title="Refresh"
                        >
                            <RefreshCw size={18} className={payslipsLoading ? 'animate-spin' : ''} />
                        </button>
                    </div>
                </div>

                {/* Loading State */}
                {payslipsLoading && (
                    <div className="text-center py-8">
                        <RefreshCw size={32} className="mx-auto mb-4 animate-spin text-blue-500" />
                        <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>Loading payslips...</p>
                    </div>
                )}

                {/* Error State */}
                {payslipsError && !payslipsLoading && (
                    <div className={`border rounded-lg p-6 text-center ${theme === 'dark' ? 'border-red-600 bg-red-900/20' : 'border-red-300 bg-red-50'}`}>
                        <AlertCircle size={32} className="mx-auto mb-4 text-red-500" />
                        <p className={theme === 'dark' ? 'text-red-400' : 'text-red-600'}>{payslipsError}</p>
                        <button
                            onClick={fetchPayslips}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Empty State */}
                {!payslipsLoading && !payslipsError && payslips.length === 0 && (
                    <div className={`text-center py-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        <FileText size={48} className="mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium mb-2">No Pay Slips Found</p>
                        <p className="text-sm">No payslips available for {selectedPayslipYear}</p>
                    </div>
                )}

                {/* Payslips List */}
                {!payslipsLoading && !payslipsError && payslips.length > 0 && (
                    <div className="space-y-4">
                        {payslips
                            .sort((a, b) => {
                                // Sort by year desc, then month desc
                                if (b.year !== a.year) return b.year - a.year;
                                return b.month - a.month;
                            })
                            .map((payslip) => (
                            <div
                                key={payslip.id}
                                className={`border rounded-lg p-5 transition-shadow hover:shadow-md ${
                                    theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'
                                }`}
                            >
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                    {/* Left side - Period and Status */}
                                    <div className="flex items-center gap-4">
                                        <div className={`p-3 rounded-lg ${
                                            theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-50'
                                        }`}>
                                            <Calendar size={24} className="text-blue-500" />
                                        </div>
                                        <div>
                                            <h4 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {getMonthName(payslip.month)} {payslip.year}
                                            </h4>
                                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {payslip.period_name || `Payslip for ${getMonthName(payslip.month)} ${payslip.year}`}
                                            </p>
                                        </div>
                                        <span className={`px-2 py-1 text-xs rounded ${
                                            payslip.status === 'generated' || payslip.status === 'locked'
                                                ? 'bg-green-100 text-green-800'
                                                : payslip.status === 'sent'
                                                ? 'bg-blue-100 text-blue-800'
                                                : payslip.status === 'viewed'
                                                ? 'bg-purple-100 text-purple-800'
                                                : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {payslip.status === 'generated' ? 'Available' :
                                             payslip.status === 'locked' ? 'Finalized' :
                                             payslip.status === 'sent' ? 'Sent' :
                                             payslip.status === 'viewed' ? 'Viewed' :
                                             payslip.status || 'Available'}
                                        </span>
                                    </div>

                                    {/* Center - Amounts */}
                                    <div className="grid grid-cols-3 gap-4 md:gap-8">
                                        <div className="text-center">
                                            <p className={`text-xs font-medium uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                Gross
                                            </p>
                                            <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {formatCurrencyAmount(payslip.gross_salary)}
                                            </p>
                                        </div>
                                        <div className="text-center">
                                            <p className={`text-xs font-medium uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                Deductions
                                            </p>
                                            <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
                                                {formatCurrencyAmount(payslip.total_deductions)}
                                            </p>
                                        </div>
                                        <div className="text-center">
                                            <p className={`text-xs font-medium uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                Net Pay
                                            </p>
                                            <p className={`text-sm font-bold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                                                {formatCurrencyAmount(payslip.net_salary)}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Right side - Actions */}
                                    <div className="flex items-center gap-2">
                                        {payslip.pdf_url && (
                                            <>
                                                <a
                                                    href={payslip.pdf_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`p-2 rounded-lg transition-colors ${
                                                        theme === 'dark'
                                                            ? 'text-gray-300 hover:bg-gray-700'
                                                            : 'text-gray-600 hover:bg-gray-100'
                                                    }`}
                                                    title="View Payslip"
                                                >
                                                    <Eye size={18} />
                                                </a>
                                                <button
                                                    onClick={() => handleDownloadPayslip(payslip.id, payslip.file_name)}
                                                    disabled={downloadingPayslip === payslip.id}
                                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                                                        downloadingPayslip === payslip.id
                                                            ? 'bg-gray-400 cursor-not-allowed'
                                                            : 'bg-blue-600 hover:bg-blue-700'
                                                    } text-white text-sm`}
                                                >
                                                    {downloadingPayslip === payslip.id ? (
                                                        <RefreshCw size={16} className="animate-spin" />
                                                    ) : (
                                                        <Download size={16} />
                                                    )}
                                                    <span className="hidden sm:inline">
                                                        {downloadingPayslip === payslip.id ? 'Downloading...' : 'Download'}
                                                    </span>
                                                </button>
                                            </>
                                        )}
                                        {!payslip.pdf_url && (
                                            <span className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                                PDF not available
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Additional details row */}
                                {payslip.viewed_at && (
                                    <div className={`mt-3 pt-3 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                            Last viewed: {new Date(payslip.viewed_at).toLocaleDateString('en-IN', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Summary Card */}
                {!payslipsLoading && !payslipsError && payslips.length > 0 && (
                    <div className={`mt-6 border rounded-lg p-4 ${theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
                        <h4 className={`text-sm font-medium mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            Year Summary ({selectedPayslipYear})
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Total Payslips</p>
                                <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    {payslips.length}
                                </p>
                            </div>
                            <div>
                                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Total Gross</p>
                                <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    {formatCurrencyAmount(payslips.reduce((sum, p) => sum + (parseFloat(p.gross_salary) || 0), 0))}
                                </p>
                            </div>
                            <div>
                                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Total Deductions</p>
                                <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
                                    {formatCurrencyAmount(payslips.reduce((sum, p) => sum + (parseFloat(p.total_deductions) || 0), 0))}
                                </p>
                            </div>
                            <div>
                                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Total Net Pay</p>
                                <p className={`text-lg font-bold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                                    {formatCurrencyAmount(payslips.reduce((sum, p) => sum + (parseFloat(p.net_salary) || 0), 0))}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return <div className="flex justify-center items-center h-64">Loading...</div>;
    }

    return (
        <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
            {/* Tab Navigation */}
            <div className={`border-b ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                <div className="px-2 py-2">
                    <div className="flex flex-wrap gap-1">
                        {tabSections.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-3 py-2 text-xs font-medium rounded border transition-colors ${
                                    activeTab === tab
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : theme === 'dark'
                                            ? 'text-gray-300 border-gray-600 hover:bg-gray-700'
                                            : 'text-gray-600 border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Tab Content */}
            <div className={theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}>
                {renderTabContent()}
            </div>

            {/* CTC Proration Detail Modal */}
            {showCTCProration && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto rounded-lg ${
                        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                    }`}>
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    CTC Proration Details - Monthwise Breakdown
                                </h3>
                                <button
                                    onClick={() => setShowCTCProration(false)}
                                    className={`p-2 rounded-full ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                                >
                                    <X size={20} className={theme === 'dark' ? 'text-white' : 'text-gray-600'} />
                                </button>
                            </div>

                            <div className="overflow-x-auto">
                                <table className={`min-w-full border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>
                                    <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                                        <tr>
                                            <th className={`px-3 py-2 text-left text-xs font-medium uppercase border-r ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Month</th>
                                            <th className={`px-3 py-2 text-left text-xs font-medium uppercase border-r ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Attended/Total Days</th>
                                            <th className={`px-3 py-2 text-left text-xs font-medium uppercase border-r ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Gross CTC</th>
                                            <th className={`px-3 py-2 text-left text-xs font-medium uppercase border-r ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Actual Gross</th>
                                            <th className={`px-3 py-2 text-left text-xs font-medium uppercase border-r ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Deductions</th>
                                            <th className={`px-3 py-2 text-left text-xs font-medium uppercase border-r ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>TDS</th>
                                            <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Net CTC</th>
                                        </tr>
                                    </thead>
                                    <tbody className={theme === 'dark' ? 'bg-gray-800' : 'bg-white'}>
                                        {generateProrationData(selectedProrationRow).map((month, index) => (
                                            <tr key={index} className={`border-t ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>
                                                <td className={`px-3 py-2 border-r text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                    {month.month}
                                                </td>
                                                <td className={`px-3 py-2 border-r text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                    {month.attendedDays}/{month.workDays}
                                                </td>
                                                <td className={`px-3 py-2 border-r text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                    {currencies[selectedCurrency].symbol}{convertCurrency(month.grossCTC.toString())}
                                                </td>
                                                <td className={`px-3 py-2 border-r text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                    {currencies[selectedCurrency].symbol}{convertCurrency(month.actualGross.toString())}
                                                </td>
                                                <td className={`px-3 py-2 border-r text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                    {currencies[selectedCurrency].symbol}{convertCurrency(month.deductions.toString())}
                                                </td>
                                                <td className={`px-3 py-2 border-r text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                    {currencies[selectedCurrency].symbol}{convertCurrency(month.tds.toString())}
                                                </td>
                                                <td className={`px-3 py-2 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                    {currencies[selectedCurrency].symbol}{convertCurrency(month.netCTC.toString())}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'} border-t-2`}>
                                        <tr>
                                            <td className={`px-3 py-3 border-r text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                TOTALS
                                            </td>
                                            <td className={`px-3 py-3 border-r text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {generateProrationData(selectedProrationRow).reduce((sum, month) => sum + month.attendedDays, 0)}/
                                                {generateProrationData(selectedProrationRow).reduce((sum, month) => sum + month.workDays, 0)}
                                            </td>
                                            <td className={`px-3 py-3 border-r text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {currencies[selectedCurrency].symbol}{convertCurrency(
                                                    generateProrationData(selectedProrationRow).reduce((sum, month) => sum + month.grossCTC, 0).toString()
                                                )}
                                            </td>
                                            <td className={`px-3 py-3 border-r text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {currencies[selectedCurrency].symbol}{convertCurrency(
                                                    generateProrationData(selectedProrationRow).reduce((sum, month) => sum + month.actualGross, 0).toString()
                                                )}
                                            </td>
                                            <td className={`px-3 py-3 border-r text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {currencies[selectedCurrency].symbol}{convertCurrency(
                                                    generateProrationData(selectedProrationRow).reduce((sum, month) => sum + month.deductions, 0).toString()
                                                )}
                                            </td>
                                            <td className={`px-3 py-3 border-r text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {currencies[selectedCurrency].symbol}{convertCurrency(
                                                    generateProrationData(selectedProrationRow).reduce((sum, month) => sum + month.tds, 0).toString()
                                                )}
                                            </td>
                                            <td className={`px-3 py-3 text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {currencies[selectedCurrency].symbol}{convertCurrency(
                                                    generateProrationData(selectedProrationRow).reduce((sum, month) => sum + month.netCTC, 0).toString()
                                                )}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Flexi Component Request Modal */}
            {showFlexiRequestModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`max-w-md w-full mx-4 rounded-lg ${
                        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                    }`}>
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    {editingRequest ? 'Edit Flexi Component Request' : 'Raise Flexi Component Request'}
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowFlexiRequestModal(false);
                                        setEditingRequest(null);
                                        setNewFlexiRequest({ title: '', amount: '', currency: 'INR', supportingDocument: null });
                                    }}
                                    className={`p-2 rounded-full ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                                >
                                    <X size={20} className={theme === 'dark' ? 'text-white' : 'text-gray-600'} />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Component Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={newFlexiRequest.title}
                                        onChange={(e) => setNewFlexiRequest({...newFlexiRequest, title: e.target.value})}
                                        placeholder="e.g., Medical Allowance, Transport Allowance"
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                                            theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300'
                                        }`}
                                    />
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Amount *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={newFlexiRequest.amount}
                                        onChange={(e) => setNewFlexiRequest({...newFlexiRequest, amount: e.target.value})}
                                        placeholder="Enter amount (positive numbers only)"
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                                            theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300'
                                        }`}
                                    />
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Currency
                                    </label>
                                    <select
                                        value={newFlexiRequest.currency}
                                        onChange={(e) => setNewFlexiRequest({...newFlexiRequest, currency: e.target.value})}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                                            theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                                        }`}
                                    >
                                        {Object.keys(currencies).map(currency => (
                                            <option key={currency} value={currency}>
                                                {currency} ({currencies[currency].symbol})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Attach Proof/Supporting Document
                                    </label>
                                    <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
                                        theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'
                                    }`}>
                                        <input
                                            type="file"
                                            id="supporting-document"
                                            accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                setNewFlexiRequest({...newFlexiRequest, supportingDocument: file});
                                            }}
                                            className="hidden"
                                        />
                                        <label
                                            htmlFor="supporting-document"
                                            className={`cursor-pointer flex flex-col items-center gap-2 ${
                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                            }`}
                                        >
                                            <Upload size={24} />
                                            <span className="text-sm">
                                                {newFlexiRequest.supportingDocument
                                                    ? newFlexiRequest.supportingDocument.name
                                                    : 'Click to upload or drag and drop'}
                                            </span>
                                            <span className="text-xs opacity-70">
                                                PNG, JPG, PDF, DOC (max 10MB)
                                            </span>
                                        </label>
                                    </div>
                                    {newFlexiRequest.supportingDocument && (
                                        <div className="mt-2 flex items-center justify-between">
                                            <span className={`text-sm ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                                                File selected: {newFlexiRequest.supportingDocument.name}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setNewFlexiRequest({...newFlexiRequest, supportingDocument: null})}
                                                className={`text-sm ${theme === 'dark' ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-500'}`}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                    <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                        Note: This request will also be visible to your reporting manager and admin.
                                    </p>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowFlexiRequestModal(false);
                                        setEditingRequest(null);
                                        setNewFlexiRequest({ title: '', amount: '', currency: 'INR', supportingDocument: null });
                                    }}
                                    className={`px-4 py-2 text-sm rounded-lg border ${
                                        theme === 'dark'
                                            ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleFlexiRequestSubmit}
                                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    {editingRequest ? 'Update Request' : 'Submit Request'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`max-w-sm w-full mx-4 rounded-lg ${
                        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                    }`}>
                        <div className="p-6">
                            <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Delete Request
                            </h3>
                            <p className={`text-sm mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                Are you sure you want to delete this flexi component request? This action cannot be undone.
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setShowDeleteConfirm(null)}
                                    className={`px-4 py-2 text-sm rounded-lg border ${
                                        theme === 'dark'
                                            ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleDeleteRequest(showDeleteConfirm)}
                                    className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};


export default EmployeePayroll;